import { create } from 'zustand'

/**
 * Живой буфер телеметрии флота для спарклайнов дашборда.
 *
 * Зачем отдельное хранилище, а не запрос к API: сервер отдаёт мгновенный снимок
 * (`GET /dashboard/summary`) и присылает точечные обновления по WS
 * (`dashboard.tick`, `bot.state`). Истории «пинг минуту назад» на бэкенде нет —
 * заводить под неё таблицу ради двух спарклайнов дорого. Поэтому историю
 * накапливает сам клиент, пока страница открыта: это честные измерения, а не
 * сглаженная выдумка, и после перезагрузки график начинается заново.
 *
 * Хранилище держит кольцевой буфер фиксированной длины: при 5-секундном тике
 * 90 точек — это последние ~7 минут.
 */

/** Одна выборка. `null` в поле = значение в этот момент неизвестно. */
export interface TelemetrySample {
  /** Момент выборки, мс. */
  at: number
  /** Сколько ботов в состоянии PLAYING. */
  playing: number
  /** Средний ping по играющим ботам, мс. `null` — играющих нет. */
  avgPing: number | null
}

export const TELEMETRY_LIMIT = 90

/**
 * Минимальный интервал между выборками. Кэш TanStack Query дёргает подписчиков
 * на каждое `bot.state` (16 ботов × тик = шторм), а спарклайну нужна ровная
 * сетка по времени, иначе «последние 7 минут» превращаются в «последние 20
 * секунд активного бота».
 */
export const TELEMETRY_MIN_STEP_MS = 4_000

interface TelemetryState {
  samples: TelemetrySample[]
  /** Добавляет выборку, если с прошлой прошло достаточно времени. */
  sample: (input: { playing: number; avgPing: number | null; at?: number }) => void
  reset: () => void
}

export const useTelemetryStore = create<TelemetryState>(set => ({
  samples: [],
  sample: input =>
    set(state => {
      const at = input.at ?? Date.now()
      const last = state.samples.at(-1)
      if (last !== undefined && at - last.at < TELEMETRY_MIN_STEP_MS) return state
      const next = [...state.samples, { at, playing: input.playing, avgPing: input.avgPing }]
      return {
        samples: next.length > TELEMETRY_LIMIT ? next.slice(-TELEMETRY_LIMIT) : next
      }
    }),
  reset: () => set({ samples: [] })
}))

/** Средний ping по ботам, у которых он известен. `null` — считать нечего. */
export function averagePing(pings: Array<number | null | undefined>): number | null {
  const known = pings.filter((ping): ping is number => typeof ping === 'number' && ping >= 0)
  if (known.length === 0) return null
  return Math.round(known.reduce((total, ping) => total + ping, 0) / known.length)
}
